import { useState, type FormEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

export type PasswordRecoveryCallback = {
  tokenHash: string | null;
  hasImplicitSession: boolean;
  isRecovery: boolean;
};

export function parsePasswordRecoveryCallback(search: string, hash: string): PasswordRecoveryCallback {
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fragment = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const type = query.get('type') ?? fragment.get('type');
  const tokenHash = query.get('token_hash') ?? fragment.get('token_hash');
  const hasImplicitSession = type === 'recovery' && Boolean(fragment.get('access_token'));
  return {
    tokenHash: type === 'recovery' ? tokenHash : null,
    hasImplicitSession,
    isRecovery: type === 'recovery' && (Boolean(tokenHash) || hasImplicitSession),
  };
}

export function PasswordRecoveryUnavailable({ message }: { message: string }) {
  return <main className="page-shell admin-auth-shell"><section className="auth-card admin-auth-card recovery-auth-card">
    <p className="eyebrow">KINSTORY / ACCOUNT SECURITY</p>
    <h1>This reset link is unavailable.</h1>
    <p className="lede">{message}</p>
    <p className="fine-print">For your protection, recovery links expire and can only be used once. Request a fresh password reset email and open it on this device.</p>
    <a className="recovery-back-link" href="/admin">Return to system admin sign in</a>
  </section></main>;
}

export function PasswordRecoveryScreen({ client, onContinue }: { client: SupabaseClient; onContinue: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage('');
    if (password.length < 8) {
      setMessage('Choose a password with at least 8 characters.');
      return;
    }
    if (password !== confirmation) {
      setMessage('The passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const { error } = await client.auth.updateUser({ password });
      if (error) {
        setMessage(error.message);
        return;
      }
      setPassword('');
      setConfirmation('');
      setComplete(true);
    } catch {
      setMessage('We could not update your password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return <main className="page-shell admin-auth-shell"><section className="auth-card admin-auth-card recovery-auth-card">
    <p className="eyebrow">KINSTORY / ACCOUNT SECURITY</p>
    {complete ? <>
      <h1>Your password is updated.</h1>
      <p className="lede">You can now continue to the KinStory system admin console.</p>
      <button onClick={onContinue}>Continue to system admin</button>
    </> : <>
      <h1>Choose a new password.</h1>
      <p className="lede">Use a password with at least 8 characters. It is sent securely to Supabase Auth and is never written to KinStory logs.</p>
      <form onSubmit={submit}>
        <label>New password<input type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <label>Confirm new password<input type="password" required minLength={8} autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        <button disabled={busy}>{busy ? 'Updating password…' : 'Update password'}</button>
      </form>
      {message && <p className="error" role="alert">{message}</p>}
    </>}
  </section></main>;
}
